import { useEffect, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth.store'

/**
 * Inicializa la sesión de Supabase UNA SOLA VEZ para toda la app.
 * Colócalo en main.tsx, envolviendo <App />.
 */
export default function AuthProvider({ children }: { children: ReactNode }) {
  const { setAuth, setProfile, setLoading, reset } = useAuthStore()

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    let mounted = true

    const loadProfile = async (userId: string) => {
      const { data } = await supabase!.from('profiles').select('*').eq('id', userId).single()
      if (mounted) {
        setProfile(data)
        setLoading(false)
      }
    }

    // Sesión inicial — carga una vez al arrancar la app
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return
      setAuth(session?.user ?? null, session)
      if (session?.user) {
        loadProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Escuchar cambios (login / logout / token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      // INITIAL_SESSION ya lo manejamos con getSession() arriba — ignorar
      if (event === 'INITIAL_SESSION') return

      if (event === 'SIGNED_OUT') {
        reset()
        setLoading(false)
        return
      }

      // SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED…
      setAuth(session?.user ?? null, session)
      if (session?.user) {
        setLoading(true)
        await loadProfile(session.user.id)
      } else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>
}
